import { useState } from 'react'
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useAdminCourses } from '@/services/admin/courses.service'
import { useAdminModules } from '@/services/admin/modules.service'
import {
  useAdminEpisodes,
  useCreateEpisode,
  useUpdateEpisode,
  useDeleteEpisode,
  Episode,
  CreateEpisodeDTO,
  UpdateEpisodeDTO,
} from '@/services/admin/episodes.service'
import { EpisodeFormDialog } from './EpisodeFormDialog'
import { useAuth } from '@/hooks/use-auth'
import { canManageCourseResource } from '@/types/auth'
import { getErrorMessage } from '@/utils/error'

const statusLabels = {
  draft: 'Rascunho',
  published: 'Publicado',
  archived: 'Arquivado',
  coming_soon: 'Em Breve',
}

const statusColors = {
  draft: 'secondary',
  published: 'default',
  archived: 'outline',
  coming_soon: 'secondary',
} as const

export function EpisodesManagement() {
  const { toast } = useToast()
  const { user } = useAuth()
  const [selectedCourseId, setSelectedCourseId] = useState<number>()
  const [selectedModuleId, setSelectedModuleId] = useState<number>()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingEpisode, setEditingEpisode] = useState<Episode | null>(null)
  const [deletingEpisode, setDeletingEpisode] = useState<Episode | null>(null)

  const { data: courses } = useAdminCourses()
  const { data: modules } = useAdminModules(selectedCourseId)
  const { data: episodes, isLoading } = useAdminEpisodes({
    moduleId: selectedModuleId,
    courseId: selectedCourseId,
  })
  const createEpisode = useCreateEpisode()
  const updateEpisode = useUpdateEpisode()
  const deleteEpisode = useDeleteEpisode()

  const handleCreate = () => {
    setEditingEpisode(null)
    setDialogOpen(true)
  }

  const handleEdit = (episode: Episode) => {
    setEditingEpisode(episode)
    setDialogOpen(true)
  }

  const handleDelete = (episode: Episode) => {
    setDeletingEpisode(episode)
  }

  const confirmDelete = async () => {
    if (!deletingEpisode) return

    try {
      await deleteEpisode.mutateAsync(deletingEpisode.id)
      toast({
        title: 'Episódio deletado',
        description: `O episódio "${deletingEpisode.title}" foi removido com sucesso.`,
      })
      setDeletingEpisode(null)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao deletar episódio',
        description: getErrorMessage(error, 'Ocorreu um erro ao deletar o episódio.'),
      })
    }
  }

  const handleSubmit = async (data: CreateEpisodeDTO | UpdateEpisodeDTO) => {
    try {
      if (editingEpisode) {
        await updateEpisode.mutateAsync({
          id: editingEpisode.id,
          data: data as UpdateEpisodeDTO,
        })
        toast({
          title: 'Episódio atualizado',
          description: 'O episódio foi atualizado com sucesso.',
        })
      } else {
        await createEpisode.mutateAsync(data as CreateEpisodeDTO)
        toast({
          title: 'Episódio criado',
          description: 'O episódio foi criado com sucesso.',
        })
      }
      setDialogOpen(false)
      setEditingEpisode(null)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: editingEpisode ? 'Erro ao atualizar episódio' : 'Erro ao criar episódio',
        description: getErrorMessage(error, 'Ocorreu um erro ao processar a solicitação.'),
      })
      throw error
    }
  }

  const handleCourseChange = (courseId: string) => {
    const id = courseId === 'all' ? undefined : Number(courseId)
    setSelectedCourseId(id)
    setSelectedModuleId(undefined)
  }

  const handleModuleChange = (moduleId: string) => {
    setSelectedModuleId(moduleId === 'all' ? undefined : Number(moduleId))
  }

  return (
    <>
      <Card className="glass-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Gerenciamento de Episódios</CardTitle>
              <CardDescription>Crie, edite e gerencie os episódios dos módulos</CardDescription>
            </div>
            <Button onClick={handleCreate} disabled={!modules || modules.length === 0}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Episódio
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium mb-2 block">Curso:</label>
              <Select
                value={selectedCourseId?.toString() || 'all'}
                onValueChange={handleCourseChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um curso" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os cursos</SelectItem>
                  {courses?.map((course) => (
                    <SelectItem key={course.id} value={course.id.toString()}>
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Módulo:</label>
              <Select
                value={selectedModuleId?.toString() || 'all'}
                onValueChange={handleModuleChange}
                disabled={!selectedCourseId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um módulo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os módulos</SelectItem>
                  {modules?.map((module) => (
                    <SelectItem key={module.id} value={module.id.toString()}>
                      {module.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !episodes || episodes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum episódio cadastrado{selectedModuleId ? ' para este módulo' : ''}.</p>
              <p className="text-sm mt-2">Clique em "Novo Episódio" para começar.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Módulo</TableHead>
                    <TableHead>Curso</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {episodes.map((episode) => (
                    <TableRow key={episode.id}>
                      <TableCell>
                        <Badge variant="outline">Ep {episode.number}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{episode.title}</div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {episode.module?.title}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {episode.module?.course?.title}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusColors[episode.status]}>
                          {statusLabels[episode.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {episode.duration ? `${episode.duration} min` : '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {canManageCourseResource(user?.role, episode.module?.course?.companyId) ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(episode)}
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(episode)}
                              title="Deletar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Somente leitura</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <EpisodeFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        episode={editingEpisode}
        preselectedModuleId={selectedModuleId}
        onSubmit={handleSubmit}
        isSubmitting={createEpisode.isPending || updateEpisode.isPending}
      />

      <AlertDialog open={!!deletingEpisode} onOpenChange={() => setDeletingEpisode(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso irá deletar permanentemente o episódio "
              {deletingEpisode?.title}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteEpisode.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
